<script setup>
import { computed, nextTick, onMounted, onUnmounted, reactive, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useStore } from "vuex";
import {
  fetchSupportChat,
  fetchSupportChats,
  createSupportChat,
  fetchSupportChatMessages,
  submitSupportMessage,
  editSupportMessage,
  setSupportChatEscalated,
  setSupportChatOpen,
  setSupportChatAssignedAdmin,
  fetchSupportAnswerSuggestions,
  fetchSupportMessagesTranslation,
  fetchAdmins,
  getMediaUrl,
  getSupportSseUrl,
  getRequestHeaders,
} from "@/services/backofficeClient";
import { ROLES } from "@/services/acl";
import { getLocalStorage } from "@/services/storage";
import UserDetailsPanel from "@/components/UserDetailsPanel.vue";

const CHAT_PAGE_SIZE = 20;
const MESSAGE_PAGE_SIZE = 30;
const TRANSLATION_MESSAGE_LIMIT = 30;
const TRANSLATION_LANGUAGES = [
  { code: "English", label: "English" },
  { code: "Persian", label: "Persian" },
];

const route = useRoute();
const router = useRouter();
const store = useStore();
const canCreateSupport = computed(() => store.getters.hasRole(ROLES.SUPPORT_CREATE));
const canEditSupport = computed(() => store.getters.hasRole(ROLES.SUPPORT_EDIT));

// ---------------------------------------------------------------------------
// Admins (loaded once on mount, kept in memory)
// ---------------------------------------------------------------------------
const admins = ref([]);
const adminsLoading = ref(false);
const adminsById = computed(() => {
  const map = {};
  admins.value.forEach((admin) => {
    map[admin.id] = admin;
  });
  return map;
});
const currentAdminId = computed(() => store.getters.currentAdmin?.id || null);

const loadAdmins = async () => {
  adminsLoading.value = true;
  try {
    const payload = await fetchAdmins({ pageSize: 200 });
    const items = Array.isArray(payload) ? payload : payload?.items;
    admins.value = (items || []).map((admin) => ({
      id: admin.id,
      name: admin.name || admin.email || admin.id,
    }));
  } catch (err) {
    console.error("Failed to load admins", err);
  } finally {
    adminsLoading.value = false;
  }
};

const adminLabel = (adminId) => {
  if (!adminId) return "Unassigned";
  return adminsById.value[adminId]?.name || adminId;
};

// ---------------------------------------------------------------------------
// Chat list state
// ---------------------------------------------------------------------------
const chats = ref([]);
const chatPagination = ref({ page: 1, totalItems: 0, totalPages: 1, hasNext: false });
const chatsLoading = ref(true);
const chatsError = ref("");
const currentChatPage = ref(1);

const filterEscalated = ref(true); // null | true
const filterOnlyOpen = ref(true); // null | true
const filterUserId = ref(route.query.userId || "");
const appliedUserId = ref("");

// Assigned-admin filter: array of selected values, where `null` means "Unassigned".
// Defaults to Unassigned + chats assigned to the current admin.
const defaultAssignedAdminIds = computed(() => {
  const ids = [null];
  if (currentAdminId.value) ids.push(currentAdminId.value);
  return ids;
});
const filterAssignedAdminIds = ref([...defaultAssignedAdminIds.value]);
const assignedFilterMenuOpen = ref(false);

const isAssignedFilterDefault = computed(() => {
  const current = new Set(filterAssignedAdminIds.value.map((v) => v ?? "null"));
  const def = new Set(defaultAssignedAdminIds.value.map((v) => v ?? "null"));
  if (current.size !== def.size) return false;
  for (const v of current) {
    if (!def.has(v)) return false;
  }
  return true;
});

const isAssignedFilterSelected = (value) =>
  filterAssignedAdminIds.value.includes(value);

const toggleAssignedFilterValue = (value) => {
  if (isAssignedFilterSelected(value)) {
    filterAssignedAdminIds.value = filterAssignedAdminIds.value.filter(
      (v) => v !== value
    );
  } else {
    filterAssignedAdminIds.value = [...filterAssignedAdminIds.value, value];
  }
  applyFilter();
};

const toggleAssignedFilterMenu = () => {
  assignedFilterMenuOpen.value = !assignedFilterMenuOpen.value;
};

const assignedFilterLabel = computed(() => {
  if (filterAssignedAdminIds.value.length === 0) return "All";
  return filterAssignedAdminIds.value
    .map((v) => (v === currentAdminId.value && v !== null ? "Me" : adminLabel(v)))
    .join(", ");
});

// ---------------------------------------------------------------------------
// New chat composer state
// ---------------------------------------------------------------------------
const creatingNewChat = ref(false);
const newChatSubject = ref("");
const newChatError = ref("");
const newChatSaving = ref(false);

// ---------------------------------------------------------------------------
// Selected chat / message thread state
// ---------------------------------------------------------------------------
const selectedChatId = ref(route.query.chatId || null);
const detachedChat = ref(null);
const selectedChat = computed(() => {
  const listed = chats.value.find((c) => c.id === selectedChatId.value);
  if (listed) return listed;
  if (detachedChat.value?.id === selectedChatId.value) return detachedChat.value;
  return null;
});

const findChatById = (chatId) =>
  chats.value.find((c) => c.id === chatId) ||
  (detachedChat.value?.id === chatId ? detachedChat.value : null) ||
  detailsPanelRef.value?.findChat(chatId) ||
  null;

const patchChat = (chatId, patch) => {
  const idx = chats.value.findIndex((c) => c.id === chatId);
  if (idx !== -1) chats.value[idx] = { ...chats.value[idx], ...patch };
  if (detachedChat.value?.id === chatId) {
    detachedChat.value = { ...detachedChat.value, ...patch };
  }
  detailsPanelRef.value?.patchChat(chatId, patch);
};

const messages = ref([]);
const messagesLoading = ref(false);
const messagesError = ref("");
const messagePage = ref(1);
const hasMoreMessages = ref(false);
const messageContainer = ref(null);

// ---------------------------------------------------------------------------
// Reply form state
// ---------------------------------------------------------------------------
const replyText = ref("");
const replySending = ref(false);
const replyInputRef = ref(null);
const replyError = ref("");

const SENDER_NAME_STORAGE_PREFIX = "mm_backoffice_support_sender_name_";
const defaultSenderName = () => store.getters.currentAdmin?.name || "Admin";
const loadSenderNameForChat = (chatId) => {
  const stored = chatId
    ? getLocalStorage()?.getItem(SENDER_NAME_STORAGE_PREFIX + chatId)
    : null;
  return stored || defaultSenderName();
};
const saveSenderNameForChat = (chatId, name) => {
  if (!chatId) return;
  getLocalStorage()?.setItem(SENDER_NAME_STORAGE_PREFIX + chatId, name);
};
const senderName = ref(defaultSenderName());
const promptSenderName = () => {
  if (typeof window === "undefined" || !window.prompt) return;
  const next = window.prompt("Send messages as", senderName.value);
  if (next === null) return;
  senderName.value = next.trim() || defaultSenderName();
  saveSenderNameForChat(selectedChatId.value, senderName.value);
};

// ---------------------------------------------------------------------------
// Escalate / Open state
// ---------------------------------------------------------------------------
const escalateLoading = ref(false);
const escalateError = ref("");
const openLoading = ref(false);
const openError = ref("");

// ---------------------------------------------------------------------------
// Suggest answer modal state
// ---------------------------------------------------------------------------
const suggestModalOpen = ref(false);
const suggestAdminInput = ref("");
const suggestions = ref([]);
const suggestLoading = ref(false);
const suggestError = ref("");

const openSuggestModal = () => {
  suggestions.value = [];
  suggestError.value = "";
  suggestAdminInput.value = replyText.value.trim();
  suggestModalOpen.value = true;
};

const closeSuggestModal = () => {
  suggestModalOpen.value = false;
  suggestAdminInput.value = "";
  suggestions.value = [];
  suggestError.value = "";
};

const suggestAnswers = async () => {
  if (!selectedChatId.value && !creatingNewChat.value) return;
  suggestLoading.value = true;
  suggestError.value = "";
  suggestions.value = [];
  try {
    const result = await fetchSupportAnswerSuggestions({
      chatId: selectedChatId.value || null,
      userId: !selectedChatId.value ? appliedUserId.value : null,
      adminInput: suggestAdminInput.value.trim() || null,
    });
    suggestions.value = result.suggestions || [];
  } catch (err) {
    suggestError.value = err?.message || "Failed to fetch suggestions.";
  } finally {
    suggestLoading.value = false;
  }
};

const applySuggestion = (text) => {
  replyText.value = text;
  closeSuggestModal();
};

// ---------------------------------------------------------------------------
// Edit state
// ---------------------------------------------------------------------------
const editingRemoteId = ref(null);
const editText = ref("");
const editSaving = ref(false);
const editError = ref("");

// ---------------------------------------------------------------------------
// Translate state
// ---------------------------------------------------------------------------
const translateMenuOpen = ref(false);
const translateLoading = ref(false);
const translateError = ref("");
const activeTranslationLanguage = ref("");
const translationsByLanguage = reactive({}); // language -> { remoteId -> translatedText }

const toggleTranslateMenu = () => {
  translateMenuOpen.value = !translateMenuOpen.value;
};

const latestTextMessages = computed(() =>
  messages.value
    .filter((msg) => msg.contentType === "text" && msg.text && msg.text.trim())
    .slice(-TRANSLATION_MESSAGE_LIMIT)
);

const translationFor = (msg) => {
  if (!activeTranslationLanguage.value || !msg) return null;
  const cache = translationsByLanguage[activeTranslationLanguage.value];
  return cache ? cache[msg.remoteId] || null : null;
};

const translateChat = async (language) => {
  translateMenuOpen.value = false;
  translateError.value = "";
  activeTranslationLanguage.value = language;

  const cache =
    translationsByLanguage[language] || (translationsByLanguage[language] = {});
  const pendingMessages = latestTextMessages.value.filter(
    (msg) => !(msg.remoteId in cache)
  );
  if (pendingMessages.length === 0) return;

  translateLoading.value = true;
  try {
    const payload = await fetchSupportMessagesTranslation(
      pendingMessages.map((msg) => msg.remoteId),
      language
    );
    const translations = Array.isArray(payload?.translations)
      ? payload.translations
      : [];
    translations.forEach((translation) => {
      if (translation?.messageId) {
        cache[translation.messageId] = translation.translatedText;
      }
    });
  } catch (err) {
    console.error("Failed to translate messages", err);
    translateError.value = err?.message || "Failed to translate messages.";
  } finally {
    translateLoading.value = false;
  }
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
const formatDate = (value) => {
  if (!value) return "";
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const year = d.getFullYear();
  const month = `${d.getMonth() + 1}`.padStart(2, "0");
  const day = `${d.getDate()}`.padStart(2, "0");
  const h = `${d.getHours()}`.padStart(2, "0");
  const m = `${d.getMinutes()}`.padStart(2, "0");
  return `${year}-${month}-${day} ${h}:${m}`;
};

const getInitials = (name) => {
  if (!name) return "?";
  return name
    .split(" ")
    .map((p) => p[0])
    .filter(Boolean)
    .join("")
    .slice(0, 2)
    .toUpperCase();
};

const parseChat = (raw) => ({
  id: raw.id,
  userId: raw.userId,
  subject: raw.subject || "—",
  isEscalated: raw.isEscalated || false,
  isClosed: raw.isClosed ?? false,
  assignedAdminId: raw.assignedAdminId || null,
  createdAt: raw.createdAt ? new Date(raw.createdAt) : null,
  lastMessageAt: raw.lastMessageAt ? new Date(raw.lastMessageAt) : null,
  unreadCount: raw.unreadCount || 0,
  user: raw.user || null,
  latestMessage: raw.latestMessage || null,
});

const parseMessage = (raw) => {
  const author = raw.author || {};
  return {
    remoteId: raw.remoteId,
    id: raw.id,
    chatId: raw.chatId,
    isSeen: raw.isSeen || false,
    createdAt: raw.createdAt ? new Date(raw.createdAt) : null,
    authorType: author.type || "unknown",
    authorUserId: author.userId || null,
    authorName: author.name || null,
    contentType: raw.content?.type || null,
    text: raw.content?.text || null,
    mediaPath: raw.content?.mediaPath || null,
  };
};

const messageAuthorLabel = (msg) => {
  if (msg.authorType === "user") return "User";
  if (msg.authorType === "ai") return msg.authorName || "AI";
  if (msg.authorType === "agent") return msg.authorName || "Agent";
  return "Unknown";
};

const isOutbound = (msg) => msg.authorType === "agent" || msg.authorType === "ai";

const mediaViewerUrl = ref("");

const openMedia = (mediaPath) => {
  const url = getMediaUrl(mediaPath);
  if (url) mediaViewerUrl.value = url;
};

const closeMedia = () => {
  mediaViewerUrl.value = "";
};

const latestMessagePreview = (chat) => {
  const msg = chat.latestMessage;
  if (!msg) return "—";
  const content = msg.content;
  if (!content) return "—";
  if (content.type === "text") return content.text || "—";
  if (content.type === "media") return "[Media]";
  return "—";
};

// ---------------------------------------------------------------------------
// Load chats
// ---------------------------------------------------------------------------
const loadChats = async (page = 1) => {
  chatsLoading.value = true;
  chatsError.value = "";
  const userId = filterUserId.value.trim();
  appliedUserId.value = userId;
  try {
    const payload = await fetchSupportChats({
      page,
      pageSize: CHAT_PAGE_SIZE,
      onlyEscalated: filterEscalated.value,
      onlyOpen: filterOnlyOpen.value,
      userId: userId || null,
      assignedAdminIds: filterAssignedAdminIds.value,
    });
    chats.value = (payload.items || []).map(parseChat);
    const p = payload.pagination || {};
    chatPagination.value = {
      page: p.page || page,
      totalItems: p.totalItems || 0,
      totalPages: p.totalPages || 1,
      hasNext: p.hasNext || false,
    };
    currentChatPage.value = page;
  } catch (err) {
    chatsError.value = err?.message || "Failed to load chats.";
  } finally {
    chatsLoading.value = false;
  }
};

// ---------------------------------------------------------------------------
// Load messages for selected chat
// ---------------------------------------------------------------------------
const loadMessages = async (chatId, page = 1, append = false) => {
  if (!chatId) return;
  messagesLoading.value = true;
  messagesError.value = "";
  try {
    const payload = await fetchSupportChatMessages(chatId, {
      page,
      pageSize: MESSAGE_PAGE_SIZE,
    });
    const parsed = (payload.items || []).map(parseMessage).reverse();
    if (append) {
      messages.value = [...parsed, ...messages.value];
    } else {
      messages.value = parsed;
      await nextTick();
      scrollToBottom();
    }
    const p = payload.pagination || {};
    messagePage.value = p.page || page;
    hasMoreMessages.value = (parsed.length === MESSAGE_PAGE_SIZE);
  } catch (err) {
    messagesError.value = err?.message || "Failed to load messages.";
  } finally {
    messagesLoading.value = false;
  }
};

const loadMoreMessages = () => {
  loadMessages(selectedChatId.value, messagePage.value + 1, true);
};

const scrollToBottom = () => {
  if (messageContainer.value) {
    messageContainer.value.scrollTop = messageContainer.value.scrollHeight;
  }
};

// ---------------------------------------------------------------------------
// Select chat
// ---------------------------------------------------------------------------
const resolveDetachedChat = async (chatId) => {
  if (!chatId) return;
  if (chats.value.some((c) => c.id === chatId)) return;
  if (detachedChat.value?.id === chatId) return;
  try {
    detachedChat.value = parseChat(await fetchSupportChat(chatId));
  } catch (err) {
    console.error("Failed to load chat", err);
  }
};

const selectChat = async (chatId) => {
  if (selectedChatId.value === chatId) return;
  creatingNewChat.value = false;
  selectedChatId.value = chatId;
  messages.value = [];
  messagePage.value = 1;
  replyText.value = "";
  replyError.value = "";
  senderName.value = loadSenderNameForChat(chatId);
  closeSuggestModal();
  editingRemoteId.value = null;
  escalateError.value = "";
  translateMenuOpen.value = false;
  translateError.value = "";
  router.replace({ query: { ...route.query, chatId } });
  resolveDetachedChat(chatId);
  await loadMessages(chatId, 1);
};

// ---------------------------------------------------------------------------
// Create a new chat for the user currently filtered in the chat list
// ---------------------------------------------------------------------------
const startNewChat = () => {
  if (!appliedUserId.value) return;
  selectedChatId.value = null;
  messages.value = [];
  replyText.value = "";
  newChatSubject.value = "";
  newChatError.value = "";
  creatingNewChat.value = true;
  router.replace({ query: { ...route.query, chatId: undefined } });
};

const cancelNewChat = () => {
  creatingNewChat.value = false;
  newChatSubject.value = "";
  newChatError.value = "";
};

const createNewChat = async () => {
  const subject = newChatSubject.value.trim();
  const text = replyText.value.trim();
  const userId = appliedUserId.value;
  if (!subject || !text || !userId) return;
  newChatSaving.value = true;
  newChatError.value = "";
  try {
    const chat = await createSupportChat({ userId, subject, isEscalated: true });
    await submitSupportMessage(chat.id, { id: crypto.randomUUID(), text });
    creatingNewChat.value = false;
    newChatSubject.value = "";
    replyText.value = "";
    await loadChats(1);
    await selectChat(chat.id);
  } catch (err) {
    newChatError.value = err?.message || "Failed to create chat.";
  } finally {
    newChatSaving.value = false;
  }
};

// ---------------------------------------------------------------------------
// Send reply
// ---------------------------------------------------------------------------
const sendReply = async () => {
  const text = replyText.value.trim();
  if (!text || !selectedChatId.value) return;
  replySending.value = true;
  replyError.value = "";
  try {
    const messageId = crypto.randomUUID();
    await submitSupportMessage(selectedChatId.value, {
      id: messageId,
      agentName: senderName.value.trim(),
      text,
    });
    replyText.value = "";
  } catch (err) {
    replyError.value = err?.message || "Failed to send message.";
  } finally {
    replySending.value = false;
    await nextTick();
    replyInputRef.value?.focus();
  }
};

const onReplyKeydown = (e) => {
  if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
    sendReply();
  }
};

// ---------------------------------------------------------------------------
// Edit message
// ---------------------------------------------------------------------------
const startEdit = (msg) => {
  editingRemoteId.value = msg.remoteId;
  editText.value = msg.text || "";
  editError.value = "";
};

const cancelEdit = () => {
  editingRemoteId.value = null;
  editText.value = "";
  editError.value = "";
};

const saveEdit = async () => {
  const text = editText.value.trim();
  if (!text || !editingRemoteId.value) return;
  editSaving.value = true;
  editError.value = "";
  try {
    await editSupportMessage(editingRemoteId.value, text);
    const idx = messages.value.findIndex((m) => m.remoteId === editingRemoteId.value);
    if (idx !== -1) {
      messages.value[idx] = { ...messages.value[idx], text };
    }
    editingRemoteId.value = null;
    editText.value = "";
  } catch (err) {
    editError.value = err?.message || "Failed to save edit.";
  } finally {
    editSaving.value = false;
  }
};

// ---------------------------------------------------------------------------
// Escalate / De-escalate
// ---------------------------------------------------------------------------
const toggleEscalate = async () => {
  if (!selectedChatId.value) return;
  const current = selectedChat.value?.isEscalated || false;
  escalateLoading.value = true;
  escalateError.value = "";
  try {
    await setSupportChatEscalated(selectedChatId.value, !current);
    patchChat(selectedChatId.value, { isEscalated: !current });
  } catch (err) {
    escalateError.value = err?.message || "Failed to update escalation.";
  } finally {
    escalateLoading.value = false;
  }
};

// ---------------------------------------------------------------------------
// Open / Close
// ---------------------------------------------------------------------------
const toggleOpen = async () => {
  if (!selectedChatId.value) return;
  const current = selectedChat.value?.isClosed ?? false;
  openLoading.value = true;
  openError.value = "";
  try {
    await setSupportChatOpen(selectedChatId.value, !current);
    patchChat(selectedChatId.value, { isClosed: !current });
  } catch (err) {
    openError.value = err?.message || "Failed to update chat status.";
  } finally {
    openLoading.value = false;
  }
};

// ---------------------------------------------------------------------------
// Assign / Unassign admin
// ---------------------------------------------------------------------------
const assignMenuOpen = ref(false);
const assignLoading = ref(false);
const assignError = ref("");

const toggleAssignMenu = () => {
  assignMenuOpen.value = !assignMenuOpen.value;
};

const assignChatToAdmin = async (adminId) => {
  if (!selectedChatId.value) return;
  assignMenuOpen.value = false;
  assignLoading.value = true;
  assignError.value = "";
  try {
    await setSupportChatAssignedAdmin(selectedChatId.value, adminId);
    patchChat(selectedChatId.value, { assignedAdminId: adminId || null });
  } catch (err) {
    assignError.value = err?.message || "Failed to update assignment.";
  } finally {
    assignLoading.value = false;
  }
};

// ---------------------------------------------------------------------------
// Filters
// ---------------------------------------------------------------------------
const applyFilter = () => {
  currentChatPage.value = 1;
  if (selectedChatId.value) {
    selectedChatId.value = null;
    messages.value = [];
  }
  cancelNewChat();
  const userId = filterUserId.value.trim();
  router.replace({ query: userId ? { userId } : {} });
  loadChats(1);
};

const applyUserIdFilter = () => {
  if (filterUserId.value.trim()) {
    filterEscalated.value = null;
    filterOnlyOpen.value = null;
  }
  applyFilter();
};

const resetFilters = () => {
  filterEscalated.value = true;
  filterOnlyOpen.value = true;
  filterUserId.value = "";
  filterAssignedAdminIds.value = [...defaultAssignedAdminIds.value];
  applyFilter();
};

// ---------------------------------------------------------------------------
// User details panel
// ---------------------------------------------------------------------------
const detailsUserId = computed(() => {
  if (creatingNewChat.value) return appliedUserId.value || null;
  return selectedChat.value?.userId || null;
});
const detailsPanelRef = ref(null);

// ---------------------------------------------------------------------------
// SSE
// ---------------------------------------------------------------------------
let sseAbort = null;
let sseMounted = true;

const isNearBottom = () => {
  if (!messageContainer.value) return true;
  const el = messageContainer.value;
  return el.scrollHeight - el.scrollTop - el.clientHeight < 80;
};

const handleSseEvent = async (data) => {
  if (data.type === "supportChatUpdated") {
    const incoming = parseChat(data.chat);
    const existing = findChatById(incoming.id);
    if (existing) {
      patchChat(incoming.id, {
        ...incoming,
        user: existing.user,
        latestMessage: existing.latestMessage,
      });
    } else if (incoming.userId && incoming.userId === detailsUserId.value) {
      detailsPanelRef.value?.prependChat(incoming);
    }
  } else if (data.type === "supportChatMessageUpdated") {
    const msg = parseMessage(data.message);

    patchChat(msg.chatId, {
      lastMessageAt: msg.createdAt,
      latestMessage: data.message,
    });

    if (msg.chatId === selectedChatId.value) {
      const msgIdx = messages.value.findIndex((m) => m.remoteId === msg.remoteId);
      const atBottom = isNearBottom();
      if (msgIdx !== -1) {
        messages.value[msgIdx] = msg;
      } else {
        messages.value = [...messages.value, msg];
        if (atBottom) {
          await nextTick();
          scrollToBottom();
        }
      }
    }
  }
};

const connectSse = async () => {
  if (!sseMounted) return;
  const abort = new AbortController();
  sseAbort = abort;

  try {
    const response = await fetch(getSupportSseUrl(), {
      headers: getRequestHeaders(),
      signal: abort.signal,
    });
    if (!response.ok || !response.body) return;

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";

    let shouldReconnect = true;
    while (shouldReconnect) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const parts = buffer.split("\n\n");
      buffer = parts.pop();
      for (const part of parts) {
        try {
          handleSseEvent(JSON.parse(part.trim()));
        } catch (_) {
          // Ignore JSON parse errors
        }
      }
    }
  } catch (_) {
    //
  }

  if (sseMounted) setTimeout(connectSse, 3000);
};

// ---------------------------------------------------------------------------
// Mount
// ---------------------------------------------------------------------------
onMounted(async () => {
  loadAdmins();
  if (filterUserId.value.trim()) {
    filterEscalated.value = null;
    filterOnlyOpen.value = null;
  }
  await loadChats(1);
  const chatIdFromQuery = route.query.chatId;
  if (chatIdFromQuery) {
    selectedChatId.value = chatIdFromQuery;
    senderName.value = loadSenderNameForChat(chatIdFromQuery);
    resolveDetachedChat(chatIdFromQuery);
    await loadMessages(chatIdFromQuery, 1);
  }
  connectSse();
});

onUnmounted(() => {
  sseMounted = false;
  sseAbort?.abort();
});
</script>

<template>
  <div class="container-fluid py-4">
    <div class="row">
      <!-- ===== LEFT: Chat list ===== -->
      <div class="col-12 col-lg-4 col-xl-3 mb-4 mb-lg-0">
        <div class="card">
          <div class="card-header pb-0">
            <div class="d-flex align-items-center justify-content-between mb-2">
              <h6 class="mb-0">Support Chats</h6>
              <button class="btn btn-sm btn-outline-secondary mb-0"
                @click="loadChats(currentChatPage); if (selectedChatId) loadMessages(selectedChatId, 1)"
                :disabled="chatsLoading" title="Refresh">
                Refresh
              </button>
            </div>

            <!-- Filters -->
            <div class="d-flex flex-wrap gap-1 mb-2">
              <button class="btn btn-sm mb-0" :class="filterEscalated ? 'btn-danger' : 'btn-outline-danger'"
                @click="filterEscalated = filterEscalated ? null : true; applyFilter()"
                title="Show only escalated chats">
                Escalated only
              </button>

              <button class="btn btn-sm mb-0" :class="filterOnlyOpen ? 'btn-info' : 'btn-outline-info'"
                @click="filterOnlyOpen = filterOnlyOpen ? null : true; applyFilter()" title="Show only open chats">
                Open only
              </button>

              <div class="position-relative">
                <button class="btn btn-sm mb-0" :class="isAssignedFilterDefault ? 'btn-outline-primary' : 'btn-primary'"
                  @click="toggleAssignedFilterMenu" title="Filter by assigned admin">
                  Assigned: {{ assignedFilterLabel }}
                </button>
                <div v-if="assignedFilterMenuOpen"
                  class="position-absolute bg-white shadow-sm rounded-2 border py-1 assign-filter-dropdown">
                  <label class="dropdown-item text-sm py-1 px-2 d-flex align-items-center gap-2 mb-0"
                    style="cursor:pointer">
                    <input type="checkbox" :checked="isAssignedFilterSelected(null)"
                      @change="toggleAssignedFilterValue(null)" />
                    Unassigned
                  </label>
                  <label v-if="currentAdminId"
                    class="dropdown-item text-sm py-1 px-2 d-flex align-items-center gap-2 mb-0" style="cursor:pointer">
                    <input type="checkbox" :checked="isAssignedFilterSelected(currentAdminId)"
                      @change="toggleAssignedFilterValue(currentAdminId)" />
                    Me ({{ store.getters.currentAdmin?.name }})
                  </label>
                  <div v-if="admins.length" class="dropdown-divider my-1"></div>
                  <label v-for="admin in admins.filter((a) => a.id !== currentAdminId)" :key="admin.id"
                    class="dropdown-item text-sm py-1 px-2 d-flex align-items-center gap-2 mb-0" style="cursor:pointer">
                    <input type="checkbox" :checked="isAssignedFilterSelected(admin.id)"
                      @change="toggleAssignedFilterValue(admin.id)" />
                    {{ admin.name }}
                  </label>
                </div>
              </div>

              <button
                v-if="filterEscalated !== true || filterOnlyOpen !== true || filterUserId || !isAssignedFilterDefault"
                class="btn btn-sm btn-outline-secondary mb-0" @click="resetFilters">
                Clear
              </button>
            </div>

            <!-- User id filter -->
            <div class="input-group input-group-sm mb-2">
              <input v-model="filterUserId" type="text" class="form-control" placeholder="Filter by user id…"
                @keydown.enter="applyUserIdFilter" />
              <button class="btn btn-outline-secondary mb-0" @click="applyUserIdFilter" title="Filter chats by user id">
                Filter
              </button>
            </div>

            <button v-if="appliedUserId" class="btn btn-sm btn-outline-primary mb-2 w-100" @click="startNewChat">
              + New chat for this user
            </button>
          </div>

          <div class="card-body p-0">
            <!-- Loading -->
            <div v-if="chatsLoading" class="p-3 text-center text-secondary text-sm">
              Loading…
            </div>

            <!-- Error -->
            <div v-else-if="chatsError" class="p-3">
              <div class="alert alert-danger text-sm py-2 mb-0">{{ chatsError }}</div>
            </div>

            <!-- Empty -->
            <div v-else-if="chats.length === 0" class="p-3 text-center text-secondary text-sm">
              No chats found.
            </div>

            <!-- List -->
            <ul v-else class="list-group list-group-flush">
              <li v-for="chat in chats" :key="chat.id" class="list-group-item list-group-item-action px-3 py-2"
                :class="{ active: selectedChatId === chat.id }" style="cursor: pointer" @click="selectChat(chat.id)">
                <div class="d-flex align-items-start gap-2">
                  <!-- Avatar -->
                  <div
                    class="avatar avatar-sm flex-shrink-0 d-flex align-items-center justify-content-center rounded-circle bg-gradient-primary text-white text-xs fw-bold"
                    style="width:32px;height:32px;font-size:11px">
                    {{ getInitials(chat.user?.name || "?") }}
                  </div>

                  <div class="flex-grow-1 overflow-hidden">
                    <div class="d-flex align-items-center justify-content-between">
                      <span class="text-sm fw-semibold text-truncate" style="max-width:120px">
                        {{ chat.user?.name || chat.userId || "User" }}
                      </span>
                      <span class="text-xs text-secondary ms-1 flex-shrink-0">
                        {{ formatDate(chat.lastMessageAt) }}
                      </span>
                    </div>

                    <div class="text-xs text-truncate"
                      :class="selectedChatId === chat.id ? 'text-white-50' : 'text-secondary'">
                      {{ chat.subject }}
                    </div>

                    <div class="d-flex align-items-center gap-1 mt-1">
                      <span v-if="chat.isClosed" class="badge badge-sm bg-gradient-secondary">Closed</span>
                      <span v-if="chat.isEscalated" class="badge badge-sm bg-gradient-danger">Escalated</span>
                      <span v-if="chat.assignedAdminId" class="badge badge-sm bg-gradient-success">{{
                        adminLabel(chat.assignedAdminId) }}</span>
                      <span v-if="chat.unreadCount > 0" class="badge badge-sm bg-gradient-info">{{ chat.unreadCount }}
                        unread</span>
                      <span class="text-xs flex-grow-1 text-truncate"
                        :class="selectedChatId === chat.id ? 'text-white-50' : 'text-muted'">
                        {{ latestMessagePreview(chat) }}
                      </span>
                    </div>
                  </div>
                </div>
              </li>
            </ul>

            <!-- Pagination -->
            <div v-if="!chatsLoading && chatPagination.totalPages > 1"
              class="px-3 py-2 d-flex justify-content-between align-items-center border-top">
              <button class="btn btn-sm btn-outline-secondary mb-0" :disabled="currentChatPage <= 1"
                @click="loadChats(currentChatPage - 1)">
                ← Prev
              </button>
              <span class="text-xs text-secondary">
                {{ currentChatPage }} / {{ chatPagination.totalPages }}
              </span>
              <button class="btn btn-sm btn-outline-secondary mb-0" :disabled="!chatPagination.hasNext"
                @click="loadChats(currentChatPage + 1)">
                Next →
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- ===== MIDDLE: Message thread ===== -->
      <div class="col-12 col-lg-8 col-xl-6 mb-4 mb-xl-0">
        <!-- New chat composer -->
        <div v-if="creatingNewChat" class="card d-flex flex-column" style="min-height:70vh">
          <div class="card-header pb-2 d-flex align-items-center justify-content-between">
            <h6 class="mb-0">New chat for user {{ appliedUserId }}</h6>
            <div class="d-flex gap-2">
              <button class="btn btn-sm btn-outline-secondary mb-0" :disabled="suggestLoading"
                @click="openSuggestModal">
                <span v-if="suggestLoading">Generating…</span>
                <span v-else>✨ Suggest</span>
              </button>
              <button class="btn btn-sm btn-outline-secondary mb-0" @click="cancelNewChat">Cancel</button>
            </div>
          </div>
          <div
            class="card-body flex-grow-1 d-flex align-items-center justify-content-center text-center text-secondary py-5">
            <div>
              <i class="ni ni-chat-round text-4xl d-block mb-3" style="font-size:3rem;opacity:.3"></i>
              Enter a subject and an initial message below to start the conversation.
            </div>
          </div>
          <div class="card-footer pt-2 pb-3">
            <div v-if="newChatError" class="alert alert-danger text-sm py-2 mb-2">{{ newChatError }}</div>
            <input v-model="newChatSubject" type="text" class="form-control form-control-sm mb-2" placeholder="Subject"
              :disabled="newChatSaving" />
            <div class="d-flex gap-2">
              <textarea ref="replyInputRef" v-model="replyText" class="form-control form-control-sm flex-grow-1"
                rows="2" placeholder="Initial message…" :disabled="newChatSaving"></textarea>
              <button v-if="canCreateSupport" class="btn btn-primary btn-sm mb-0 flex-shrink-0"
                :disabled="newChatSaving || !newChatSubject.trim() || !replyText.trim()" @click="createNewChat">
                <span v-if="newChatSaving">Creating…</span>
                <span v-else><i class="ni ni-send me-1"></i>Create chat & send</span>
              </button>
            </div>
          </div>
        </div>

        <!-- No chat selected -->
        <div v-else-if="!selectedChatId" class="card">
          <div class="card-body text-center text-secondary py-5">
            <i class="ni ni-chat-round text-4xl d-block mb-3" style="font-size:3rem;opacity:.3"></i>
            Select a chat to view messages.
          </div>
        </div>

        <!-- Chat panel -->
        <div v-else class="card d-flex flex-column" style="min-height:70vh">
          <!-- Chat header -->
          <div class="card-header pb-2 d-flex align-items-center justify-content-between flex-wrap gap-2">
            <div>
              <div class="d-flex align-items-center gap-2">
                <h6 class="mb-0">
                  {{ selectedChat?.subject || "Chat" }}
                </h6>
                <span v-if="selectedChat?.isClosed ?? false" class="badge bg-gradient-secondary">Closed</span>
                <span v-if="selectedChat?.isEscalated" class="badge bg-gradient-danger">Escalated</span>
              </div>
            </div>

            <div class="d-flex gap-2 align-items-center flex-wrap">
              <div v-if="escalateError" class="text-danger text-xs">{{ escalateError }}</div>
              <div v-if="openError" class="text-danger text-xs">{{ openError }}</div>
              <div v-if="assignError" class="text-danger text-xs">{{ assignError }}</div>
              <div v-if="translateError" class="text-danger text-xs">{{ translateError }}</div>
              <div v-if="canEditSupport" class="position-relative">
                <button class="btn btn-xs btn-outline-secondary mb-0" :disabled="assignLoading"
                  @click="toggleAssignMenu">
                  <span v-if="assignLoading">…</span>
                  <span v-else>👤 {{ adminLabel(selectedChat?.assignedAdminId) }}</span>
                </button>
                <div v-if="assignMenuOpen"
                  class="position-absolute bg-white shadow-sm rounded-2 border py-1 assign-dropdown">
                  <button class="dropdown-item text-sm py-1 px-2" :class="{ 'fw-bold': !selectedChat?.assignedAdminId }"
                    @click="assignChatToAdmin(null)">
                    Unassigned
                  </button>
                  <div v-if="admins.length" class="dropdown-divider my-1"></div>
                  <button v-for="admin in admins" :key="admin.id" class="dropdown-item text-sm py-1 px-2"
                    :class="{ 'fw-bold': selectedChat?.assignedAdminId === admin.id }"
                    @click="assignChatToAdmin(admin.id)">
                    {{ admin.name }}
                  </button>
                </div>
              </div>
              <div class="position-relative">
                <button class="btn btn-xs btn-outline-secondary mb-0" :disabled="translateLoading"
                  @click="toggleTranslateMenu">
                  <span v-if="translateLoading">Translating…</span>
                  <span v-else>🌐 Translate</span>
                </button>
                <div v-if="translateMenuOpen"
                  class="position-absolute bg-white shadow-sm rounded-2 border py-1 translate-dropdown">
                  <button v-for="language in TRANSLATION_LANGUAGES" :key="language.code"
                    class="dropdown-item text-sm py-1 px-2" @click="translateChat(language.code)">
                    {{ language.label }}
                  </button>
                </div>
              </div>
              <button v-if="canEditSupport" class="btn btn-xs mb-0"
                :class="selectedChat?.isEscalated ? 'btn-success' : 'btn-outline-danger'" :disabled="escalateLoading"
                @click="toggleEscalate">
                <span v-if="escalateLoading">…</span>
                <span v-else-if="selectedChat?.isEscalated">
                  <i class="ni ni-check-bold me-1"></i>De-escalate
                </span>
                <span v-else>
                  <i class="ni ni-notification-70 me-1"></i>Escalate
                </span>
              </button>
              <button v-if="canEditSupport" class="btn btn-xs mb-0"
                :class="(selectedChat?.isClosed ?? true) ? 'btn-outline-secondary' : 'btn-success'"
                :disabled="openLoading" @click="toggleOpen">
                <span v-if="openLoading">…</span>
                <span v-else-if="selectedChat?.isClosed ?? false">
                  <i class="ni ni-fat-remove me-1"></i>Reopen chat
                </span>
                <span v-else>
                  <i class="ni ni-check-bold me-1"></i>Close chat
                </span>
              </button>
            </div>
          </div>

          <!-- Messages area -->
          <div ref="messageContainer" class="card-body flex-grow-1 overflow-auto py-3"
            style="max-height:50vh;min-height:200px">
            <!-- Load more -->
            <div v-if="hasMoreMessages" class="text-center mb-3">
              <button class="btn btn-sm btn-outline-secondary mb-0" :disabled="messagesLoading"
                @click="loadMoreMessages">
                {{ messagesLoading ? "Loading…" : "Load earlier messages" }}
              </button>
            </div>

            <div v-if="messagesLoading && messages.length === 0" class="text-center text-secondary text-sm py-4">
              Loading messages…
            </div>

            <div v-else-if="messagesError" class="alert alert-danger text-sm py-2">{{ messagesError }}</div>

            <div v-else-if="messages.length === 0" class="text-center text-secondary text-sm py-4">
              No messages yet.
            </div>

            <!-- Message bubbles -->
            <div v-for="msg in messages" :key="msg.remoteId || msg.id" class="mb-3">
              <!-- Inline edit mode -->
              <div v-if="editingRemoteId === msg.remoteId" class="d-flex flex-column gap-1">
                <div class="text-xs text-secondary mb-1">
                  Editing message by <strong>{{ messageAuthorLabel(msg) }}</strong>
                </div>
                <textarea v-model="editText" class="form-control form-control-sm" rows="3"
                  :disabled="editSaving"></textarea>
                <div v-if="editError" class="text-danger text-xs">{{ editError }}</div>
                <div class="d-flex gap-2">
                  <button class="btn btn-sm btn-primary mb-0" :disabled="editSaving || !editText.trim()"
                    @click="saveEdit">{{ editSaving ? "Saving…" : "Save" }}</button>
                  <button class="btn btn-sm btn-outline-secondary mb-0" @click="cancelEdit">Cancel</button>
                </div>
              </div>

              <!-- Normal bubble -->
              <div v-else class="d-flex" :class="isOutbound(msg) ? 'justify-content-end' : 'justify-content-start'">
                <div class="px-3 py-2 rounded-3 text-sm position-relative" style="max-width:75%" :class="{
                  'bg-gradient-primary text-white': isOutbound(msg),
                  'bg-gray-100': !isOutbound(msg),
                }">
                  <!-- Author label -->
                  <div class="fw-semibold mb-1" style="font-size:11px"
                    :class="isOutbound(msg) ? 'text-white-50' : 'text-secondary'">
                    {{ messageAuthorLabel(msg) }}
                    <span v-if="msg.authorType === 'ai'" class="ms-1 badge bg-light text-dark"
                      style="font-size:9px">AI</span>
                  </div>

                  <!-- Content -->
                  <div v-if="msg.contentType === 'text'" style="white-space:pre-wrap">{{ msg.text }}</div>
                  <div v-else-if="msg.contentType === 'media'">
                    <img v-if="getMediaUrl(msg.mediaPath)" :src="getMediaUrl(msg.mediaPath)" class="rounded"
                      style="max-width:100%;max-height:240px;display:block;cursor:pointer"
                      @click="openMedia(msg.mediaPath)" />
                    <span v-else class="text-xs fst-italic">[Media attachment]</span>
                  </div>
                  <div v-else class="text-xs fst-italic">[Empty message]</div>

                  <!-- Translation result -->
                  <div v-if="translationFor(msg)" class="mt-2 pt-2 text-xs translation-block">
                    <span class="fw-semibold" :class="isOutbound(msg) ? 'text-white-50' : 'text-secondary'">
                      Translation:
                    </span>{{ translationFor(msg) }}
                  </div>

                  <!-- Footer: timestamp + action buttons -->
                  <div class="d-flex align-items-center gap-2 mt-1" style="font-size:10px"
                    :class="isOutbound(msg) ? 'text-white-50' : 'text-muted'">
                    <span>{{ formatDate(msg.createdAt) }}</span>
                    <button v-if="isOutbound(msg) && msg.contentType === 'text' && canEditSupport"
                      class="btn btn-sm p-0 ms-auto mb-0 border-0 p-1" style="font-size:11px;background:white"
                      title="Edit message" @click="startEdit(msg)">
                      <i class="ni ni-settings-gear-65"></i> edit
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Reply form -->
          <div class="card-footer pt-2 pb-3">
            <div v-if="replyError" class="alert alert-danger text-sm py-2 mb-2">{{ replyError }}</div>

            <div class="d-flex gap-2">
              <textarea ref="replyInputRef" v-model="replyText" class="form-control form-control-sm flex-grow-1"
                rows="2" placeholder="Type a reply… (Ctrl+Enter to send)" :disabled="replySending"
                @keydown="onReplyKeydown"></textarea>
              <div class="d-flex flex-column gap-1 flex-shrink-0">
                <button class="btn btn-outline-secondary btn-sm mb-0" :disabled="replySending" @click="openSuggestModal"
                  title="Get AI answer suggestions">
                  ✨ Suggest
                </button>
                <button v-if="canCreateSupport" class="btn btn-primary btn-sm mb-0"
                  :disabled="replySending || !replyText.trim()" @click="sendReply">
                  <span v-if="replySending">Sending…</span>
                  <span v-else><i class="ni ni-send me-1"></i>Send</span>
                </button>
                <a href="#" class="text-xs text-secondary text-decoration-none text-truncate d-block text-center"
                  :title="`Sending as ${senderName} — click to change`" @click.prevent="promptSenderName">
                  Sending as {{ senderName }}
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- ===== RIGHT: User details ===== -->
      <div class="col-12 col-xl-3">
        <UserDetailsPanel ref="detailsPanelRef" :user-id="detailsUserId" :active-chat-id="selectedChatId"
          :fallback-name="selectedChat?.user?.name || ''" empty-message="Select a chat to see the user."
          @select-chat="selectChat" />
      </div>
    </div>

    <!-- ===== Suggest Answer Modal ===== -->
    <Teleport to="body">
      <div v-if="suggestModalOpen" class="suggest-modal-backdrop" @click.self="closeSuggestModal">
        <div class="suggest-modal-dialog card shadow-lg">
          <div class="card-header pb-2 d-flex align-items-center justify-content-between">
            <h6 class="mb-0">✨ AI Answer Suggestions</h6>
            <button class="btn btn-sm btn-outline-secondary mb-0" @click="closeSuggestModal">✕</button>
          </div>

          <div class="card-body">
            <!-- Input fields -->
            <div class="mb-3">
              <label class="form-label text-xs fw-semibold text-secondary mb-1">Admin input <span
                  class="fw-normal">(optional — guide the AI)</span></label>
              <textarea v-model="suggestAdminInput" class="form-control form-control-sm" rows="2"
                placeholder="e.g. User is asking about refund policy" :disabled="suggestLoading"
                style="resize:none"></textarea>
            </div>

            <button class="btn btn-primary btn-sm mb-0 w-100" :disabled="suggestLoading" @click="suggestAnswers">
              <span v-if="suggestLoading">Generating…</span>
              <span v-else>Get suggestions</span>
            </button>

            <div v-if="suggestError" class="alert alert-danger text-sm py-2 mt-3 mb-0">{{ suggestError }}</div>

            <!-- Suggestions list -->
            <div v-if="suggestions.length > 0" class="mt-3 d-flex flex-column gap-3">
              <div v-for="(s, i) in suggestions" :key="i" class="border rounded p-2">
                <div class="text-xs text-secondary fw-semibold mb-2">Suggestion {{ i + 1 }}</div>
                <div class="d-flex flex-column gap-2">
                  <div v-for="(text, lang) in s" :key="lang">
                    <div class="text-xxs text-secondary fw-bold mb-1">{{ lang.toUpperCase() }}</div>
                    <button class="btn btn-outline-secondary btn-sm mb-0 text-start w-100"
                      style="white-space:pre-wrap;text-align:left;font-size:13px;line-height:1.5"
                      @click="applySuggestion(text)">
                      {{ text }}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- ===== Media Viewer ===== -->
    <Teleport to="body">
      <div v-if="mediaViewerUrl" class="media-viewer-backdrop" @click.self="closeMedia">
        <img :src="mediaViewerUrl" alt="media preview" class="media-viewer-image" />
        <button type="button" class="btn btn-light btn-sm mb-0 media-viewer-close" @click="closeMedia">
          Close
        </button>
      </div>
    </Teleport>
  </div>
</template>

<style scoped>
.bg-gray-100 {
  background-color: #f0f2f5;
}

.suggest-modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 1050;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}

.suggest-modal-dialog {
  width: 100%;
  max-width: 520px;
  max-height: 85vh;
  overflow-y: auto;
}

.list-group-item.active {
  background-color: #5e72e4;
  border-color: #5e72e4;
  color: #fff;
}

.list-group-item.active .text-secondary,
.list-group-item.active .text-muted {
  color: rgba(255, 255, 255, 0.65) !important;
}

.list-group-item-action:hover:not(.active) {
  background-color: #f8f9fa;
}

.translate-dropdown {
  top: 100%;
  right: 0;
  z-index: 1000;
  min-width: 110px;
}

.assign-dropdown {
  top: 100%;
  left: 0;
  z-index: 100;
  min-width: 160px;
  max-height: 260px;
  overflow-y: auto;
}

.assign-filter-dropdown {
  top: 100%;
  left: 0;
  z-index: 1000;
  min-width: 200px;
  max-height: 260px;
  overflow-y: auto;
}

.translation-block {
  border-top: 1px solid rgba(0, 0, 0, 0.12);
}

.media-viewer-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.75);
  z-index: 1060;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2.5rem;
}

.media-viewer-image {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  border-radius: 0.5rem;
}

.media-viewer-close {
  position: absolute;
  top: 1rem;
  right: 1rem;
}
</style>
